import { useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import { useAuth } from '../auth/AuthContext';

export function PasswordSetup() {
  const { completePasswordSetup, finishPasswordSetup, skipPasswordSetup, signOut, passwordSetupMode } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [settingPassword, setSettingPassword] = useState(passwordSetupMode !== 'otp_first_login');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordUpdated, setPasswordUpdated] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password.length < 8) {
      setError('Password must contain at least 8 characters.');
      return;
    }
    if (password !== confirmation) {
      setError('The passwords do not match.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await completePasswordSetup(password);
      setPassword('');
      setConfirmation('');
      setPasswordUpdated(true);
    } catch (setupError) {
      setError(setupError instanceof Error ? setupError.message : 'Unable to save your password.');
    } finally {
      setSubmitting(false);
    }
  };

  const skip = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await skipPasswordSetup();
    } catch (skipError) {
      setError(skipError instanceof Error ? skipError.message : 'Unable to continue without setting a password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default', p: 2 }}>
      <Paper variant="outlined" sx={{ width: '100%', maxWidth: 440, p: 3 }}>
        <Stack spacing={2.5}>
          <Stack spacing={1} alignItems="center" textAlign="center">
            <SportsTennisIcon color="primary" sx={{ fontSize: 44 }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {passwordSetupMode === 'recovery'
                ? 'Set New Password'
                : passwordSetupMode === 'otp_first_login'
                  ? 'You are signed in'
                  : 'Set Your CourtCaptain Password'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {passwordSetupMode === 'recovery'
                ? 'Choose a new password for your CourtCaptain account.'
                : passwordSetupMode === 'otp_first_login'
                  ? 'You can keep using email codes, or add a password for future sign-ins.'
                  : 'Choose the password you will use for future CourtCaptain sign-ins.'}
            </Typography>
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
          {passwordUpdated ? (
            <Stack spacing={2}>
              <Alert severity="success">Your password has been updated successfully.</Alert>
              <Button variant="contained" onClick={finishPasswordSetup}>Continue to CourtCaptain</Button>
            </Stack>
          ) : !settingPassword ? (
            <Stack spacing={2}>
              <Button variant="contained" onClick={() => setSettingPassword(true)} disabled={submitting}>
                Set a Password
              </Button>
              <Button variant="outlined" onClick={skip} disabled={submitting}>
                {submitting ? <CircularProgress color="inherit" size={22} /> : 'Not Now'}
              </Button>
            </Stack>
          ) : <Box component="form" onSubmit={submit}>
            <Stack spacing={2}>
              <TextField label="New Password" type="password" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} required />
              <TextField label="Confirm Password" type="password" autoComplete="new-password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} required />
              <Button type="submit" variant="contained" fullWidth disabled={submitting}>
                {submitting ? <CircularProgress color="inherit" size={22} /> : 'Save Password'}
              </Button>
              {passwordSetupMode === 'otp_first_login' ? (
                <Button variant="text" disabled={submitting} onClick={() => setSettingPassword(false)}>Back</Button>
              ) : (
                <Button variant="text" onClick={() => void signOut()}>Cancel and Sign Out</Button>
              )}
            </Stack>
          </Box>}
        </Stack>
      </Paper>
    </Box>
  );
}
